import { Component, ElementRef, HostListener, inject, Input, ViewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { FGDS_SEARCHABLE_PRODUCT_TYPES, LocalizeProductNamePipe, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { FgdsProject } from "@domain/project/fgds-project";
import { FgdsProductListAddService } from "@domain/project/product-list/fgds-product-list-add.service";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { FgdsProductSearchItemComponent } from "@project/floorplanner/floorplan-menu/fgds/fgds-product-search-item/fgds-product-search-item.component";
import { combineLatest, first, map } from "rxjs";

@Component({
  selector: "app-fgds-product-list-search",
  templateUrl: "./fgds-product-list-search.component.html",
  styleUrl: "./fgds-product-list-search.component.scss",
  imports: [UiKitModule, FilterProductComponent, FgdsProductSearchItemComponent],
  providers: [LocalizeProductNamePipe],
  standalone: true,
})
export class FgdsProductListSearchComponent {
  private productService = inject(ProductService);
  private localizeProductNamePipe = inject(LocalizeProductNamePipe);
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private fgdsProductListAddService = inject(FgdsProductListAddService);

  @Input({ required: true }) set project(value: FgdsProject) {
    this._project = value;
    this.filteredResults = [];
    this.searchValue = "";
    this.isOverlayOpen = false;
  }

  @ViewChild("searchContainer") private searchContainer!: ElementRef;

  protected readonly canEditProductList = toSignal(
    combineLatest([this.userService.user$, this.projectService.hasUserWritePermission$]).pipe(
      map(([user, hasWritePermission]) => !user.readOnlyAccess && hasWritePermission === true),
    ),
    { initialValue: false },
  );

  private _project!: FgdsProject;
  private products: Product[] = [];
  protected filteredResults: Product[] = [];
  protected searchValue = "";
  protected isOverlayOpen = false;

  constructor() {
    this.collectProducts();
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.searchContainer?.nativeElement.contains(event.target)) {
      this.isOverlayOpen = false;
    }
  }

  protected openOverlayIfHasValue(): void {
    if (this.searchValue) {
      this.isOverlayOpen = true;
    }
  }

  protected search(value: string): void {
    this.searchValue = value;
    if (!value) {
      this.filteredResults = [];
      this.isOverlayOpen = false;
      return;
    }
    this.filteredResults = this.products.filter((product) => this.productService.searchFilter(product, value));
    this.isOverlayOpen = true;
  }

  protected addProductToBom(product: Product): void {
    if (!this.canEditProductList()) return;
    const name = this.localizeProductNamePipe.transform(product);
    this.fgdsProductListAddService.addProductToProductList(product, this._project, name);
  }

  private collectProducts(): void {
    this.productService
      .getAvailableProducts()
      .pipe(
        map((products) => Product.sort(products)),
        first(),
      )
      .subscribe((products) => {
        this.products = products;
      });
  }

  protected readonly floorplanProductTypes = FGDS_SEARCHABLE_PRODUCT_TYPES;
}
